import { useNavigation } from "@react-navigation/native";
import { useCallback, useContext, useEffect, useRef } from "react";
import { ConfirmationContext } from "webContext/Confirmation.context";

/**
 * useUnsavedChangesGuard
 * Modular hook for unsaved changes protection.
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes.
 * @param {string} globalFlag - Unique global flag for sidebar interception (e.g., '__orgSettingsUnsavedChanges').
 */
export default function useUnsavedChangesGuard(
  hasUnsavedChanges,
  globalFlag = "__unsavedChanges",
) {
  const navigation = useNavigation();
  const confirmNavigationRef = useRef(false);
  const { showConfirmation } = useContext(ConfirmationContext);

  // Set global flag for sidebar interception
  useEffect(() => {
    window[globalFlag] = hasUnsavedChanges;
    return () => {
      window[globalFlag] = false;
    };
  }, [hasUnsavedChanges, globalFlag]);

  // Navigation guard for in-app navigation
  useEffect(() => {
    if (!navigation) return;
    const unsubscribe = navigation.addListener("beforeRemove", (e) => {
      if (!hasUnsavedChanges || confirmNavigationRef.current) {
        return;
      }
      e.preventDefault();
      showConfirmation({
        prompt:
          "You have unsaved changes. Are you sure you want to leave this page?",
        onConfirm: () => {
          confirmNavigationRef.current = true;
          navigation.dispatch(e.data.action);
        },
        onCancel: () => {
          confirmNavigationRef.current = false;
        },
      });
    });
    return unsubscribe;
  }, [navigation, hasUnsavedChanges, showConfirmation]);

  // Wrapper for navigation.navigate
  // Use this for any navigation action (button, link, redirect) from your page/component
  // Example: navigateWithGuard("TargetScreen", { param1: value })
  // Ensures unsaved changes modal appears before navigating away
  const navigateWithGuard = useCallback(
    (routeName, params) => {
      if (!hasUnsavedChanges) {
        navigation.navigate(routeName, params);
        return;
      }
      showConfirmation({
        prompt:
          "You have unsaved changes. Are you sure you want to leave this page?",
        onConfirm: () => {
          window[globalFlag] = false;
          navigation.navigate(routeName, params);
        },
        onCancel: () => {},
      });
    },
    [hasUnsavedChanges, navigation, showConfirmation, globalFlag],
  );

  // Wrapper for navigation.push
  // Use this for stack navigation (push new screen) from your page/component
  // Example: pushWithGuard("TargetScreen", { param1: value })
  // Ensures unsaved changes modal appears before pushing new screen
  const pushWithGuard = useCallback(
    (routeName, params) => {
      if (!hasUnsavedChanges) {
        navigation.push(routeName, params);
        return;
      }
      showConfirmation({
        prompt:
          "You have unsaved changes. Are you sure you want to leave this page?",
        onConfirm: () => {
          window[globalFlag] = false;
          navigation.push(routeName, params);
        },
        onCancel: () => {},
      });
    },
    [hasUnsavedChanges, navigation, showConfirmation, globalFlag],
  );

  return { navigateWithGuard, pushWithGuard };
}
